import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

// The site the native app stands in for. Its links (the Spotify login
// redirect, shared artist links) open the app instead of the browser once
// Android App Links / iOS Universal Links are verified for it (#300, #305).
export const WEB_ORIGIN = 'https://nexxup.music';

// The in-app path for a link that opened the app, or null if it isn't one
// of the site's.
export function appLinkPath(url) {
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.origin !== WEB_ORIGIN) return null;
  return parsed.pathname + parsed.search + parsed.hash;
}

// Wires up the Android back button and links that open the app. Renders
// nothing; it sits inside <Router> because it navigates.
export default function NativeNavigation() {
  // useNavigate() returns a new function on every route change, and this
  // effect must run once: re-running it would re-read the Android launch
  // link below and send the user back to it after every navigation.
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    const open = (url) => {
      const path = appLinkPath(url);
      if (!path) return;
      // A cold start can report the same link twice (getLaunchUrl and a
      // held appUrlOpen on iOS).
      const here = window.location.pathname + window.location.search + window.location.hash;
      if (path !== here) navigateRef.current(path);
    };

    const listeners = [
      // Without a listener the plugin steps back through history but does
      // nothing on the first page. There, send the app to the background
      // like other Android apps do.
      App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) window.history.back();
        else App.minimizeApp();
      }),
      // A link opened while the app is running. The plugin holds the event
      // until a listener is added, so one that arrives before mount isn't lost.
      App.addListener('appUrlOpen', ({ url }) => open(url)),
    ];
    // A link that launched the app from cold only shows up here on Android.
    App.getLaunchUrl()
      .then((launch) => launch?.url && open(launch.url))
      .catch(() => {});

    return () => {
      listeners.forEach((pending) => pending.then((listener) => listener.remove()));
    };
  }, []);

  return null;
}
